import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../utils/Axios"; // Ensure you import axios for API requests
import { BeatLoader } from "react-spinners";

const VerifyOTP = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedTime = localStorage.getItem("timeLeft");
    return savedTime ? JSON.parse(savedTime) : 600;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!email || timeLeft <= 0) {
      navigate("/login");
    }

    // Start the timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 0) {
          localStorage.setItem("timeLeft", prev - 1);
          return prev - 1;
        } else {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, timeLeft, navigate]);

  const formatTimeLeft = () => {
    const minutes = String(Math.floor(timeLeft / 60)).padStart(2, "0");
    const seconds = String(timeLeft % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!otp) {
      toast.error("Please enter the OTP!");
      return;
    }
    if (!/^\d+$/.test(otp)) {
      toast.error("OTP must be numeric!");
      return;
    }
    if (otp.length !== 6) {
      toast.error("OTP must be 6 digits!");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        "/register-otp",
        { email, otp, password },
        { headers: { accept: "application/json" } }
      );

      toast.success(response.data.message);
      navigate("/login");
    } catch (error) {
      console.error("Error during OTP verification:", error);
      toast.error(error.response?.data?.message || "Failed to verify OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 relative p-5 md:p-0">
      <div className="absolute inset-0">
        {/* Background Elements */}
        <div className="hidden md:block absolute top-10 left-10 w-[300px] h-[300px] bg-green-400 rounded-full blur-xl opacity-20"></div>
        <div className="hidden md:block absolute top-40 right-20 w-[200px] h-[200px] bg-yellow-500 rounded-full blur-xl opacity-20"></div>
        <div className="hidden md:block absolute bottom-20 left-40 w-[250px] h-[250px] bg-brown-500 rounded-full blur-xl opacity-20"></div>
        <div className="hidden md:block absolute bottom-10 right-10 w-[150px] h-[150px] bg-green-600 rounded-full blur-xl opacity-20"></div>
      </div>

      <div className="relative w-full bg-white text-gray-700 p-6 rounded-lg md:rounded-3xl shadow-lg max-w-sm md:max-w-md lg:max-w-lg md:p-8 lg:p-10">
        <h1 className="text-2xl font-bold text-center mb-6 md:text-3xl lg:text-4xl">
          <span className="text-gray-700">Verify Email</span>
        </h1>

        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-lg flex items-start">
          <div className="flex-shrink-0">
            <i className="ri-notification-3-line bg-yellow-400 p-1 rounded-full h-6 w-6 text-yellow-100" aria-hidden="true"></i>
          </div>
          <div className="ml-3">
            <p className="font-medium">Please check your email!</p>
            <p className="mt-1 text-sm">
              An OTP has been sent to your registered email address. Use it to verify your identity.
            </p>
          </div>
        </div>

        <p className="text-left mb-6 text-sm font-semibold">
          Please enter the <span className="text-green-600">OTP</span> sent to your registered email.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label htmlFor="email" className="block text-left font-semibold mb-1 text-gray-600">
              Email
            </label>
            <input
              type="text"
              id="email"
              name="email"
              value={email}
              disabled
              className="w-full bg-gray-100 border border-green-400 py-3 px-4 text-base rounded-md focus:outline-none transition-all"
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="block text-left font-semibold mb-1 text-gray-600">
              Create Password
            </label>
            <input
              type="text"
              id="password"
              name="password"
              value={password}
              placeholder="Enter your password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-gray-100 border border-green-400 py-3 px-4 text-base rounded-md focus:outline-none transition-all"
            />
          </div>

          <div className="relative">
            <label htmlFor="otp" className="block text-left font-semibold mb-1 text-gray-600">
              OTP
            </label>
            <input
              type="text"
              id="otp"
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full bg-gray-50 border border-green-400 py-3 px-4 text-base rounded-md focus:outline-none transition-all"
              placeholder="Enter OTP"
            />
            <p className="absolute right-2 top-[50%] text-center text-sm text-gray-600">
              <i className="ri-timer-line text-yellow-500 text-xl mr-2"></i> {formatTimeLeft()}
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full px-4 py-3 text-white rounded-md font-semibold transition ${isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-500 hover:bg-green-600"
              }`}
          >
            {isSubmitting ? (
              <div className="flex justify-center items-center gap-2">
                <BeatLoader size={8} color="#fff" />
                <span>Verifying...</span>
              </div>
            ) : (
              "Verify Email"
            )}
          </button>

          <div className="flex justify-center mt-6 text-sm text-gray-600">
            <span>Don't have an account?</span>
            <Link to="/register" className="text-green-600 font-semibold hover:text-green-700 transition ml-1">
              Sign Up
            </Link>
          </div>
        </form>
      </div>
    </div >
  );
};

export default VerifyOTP;
